import {
  DRONE_HEIGHT,
  DRONE_WIDTH,
  DRONE_X,
  type GameState,
  GROUND_Y,
  OBSTACLE_WIDTH,
  type Obstacle,
  WORLD_HEIGHT,
  WORLD_WIDTH,
} from "@/lib/flappy-drone/engine";

/**
 * The Flappy Drone scene, drawn in one colour.
 *
 * Everything is stroked or filled with the single `color` the caller reads off
 * the canvas element, which is `currentColor` — so the game inherits the
 * page's foreground and flips with the theme without knowing the theme exists.
 * The context is expected to already be scaled to world units.
 */

/**
 * Where the scene is being watched from. At rest the view is pulled right in
 * on the drone; starting a run widens it out to the whole site.
 */
export type Camera = {
  zoom: number;
  centerX: number;
  centerY: number;
  /** One screen pixel, in world units — strokes stay hairline at any zoom. */
  hairline: number;
};

export type RenderOptions = {
  /** Resolved `currentColor` of the canvas element. */
  color: string;
  best: number;
  /** Suppresses the drifting skyline; the gameplay itself is unaffected. */
  reducedMotion: boolean;
  camera: Camera;
  /** 0 while only the drone is shown, 1 once the site has faded in. */
  reveal: number;
};

const SKYLINE_PARALLAX = 0.35;

/**
 * Keeps the score clear of the strip's faded ends — the component masks the
 * outer tenth of the canvas, and a number that dissolves is worse than none.
 */
const HUD_INSET = 64;

export function render(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  options: RenderOptions
): void {
  const { camera, reveal } = options;

  ctx.clearRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
  ctx.save();
  ctx.strokeStyle = options.color;
  ctx.fillStyle = options.color;
  ctx.lineJoin = "round";

  ctx.save();
  ctx.translate(WORLD_WIDTH / 2, WORLD_HEIGHT / 2);
  ctx.scale(camera.zoom, camera.zoom);
  ctx.translate(-camera.centerX, -camera.centerY);
  ctx.lineWidth = camera.hairline;

  // The site arrives with the widening view; at rest there is only the drone.
  if (reveal > 0) {
    ctx.globalAlpha = reveal;
    drawSkyline(ctx, options.reducedMotion ? 0 : state.distance);
    for (const obstacle of state.obstacles) {
      drawObstacle(ctx, obstacle);
    }
    drawGround(ctx, state.distance);
    ctx.globalAlpha = 1;
  }
  drawDrone(ctx, state);
  ctx.restore();

  // The readout is chrome rather than scenery, so it ignores the camera.
  if (reveal > 0) {
    ctx.globalAlpha = reveal;
    ctx.lineWidth = 1;
    drawScore(ctx, state.score, options.best);
    if (state.phase === "over") {
      drawGameOver(ctx);
    }
  }

  ctx.restore();
}

/** Distant tower cranes and half-built frames, thin so they read as far off. */
function drawSkyline(ctx: CanvasRenderingContext2D, distance: number): void {
  ctx.save();
  ctx.globalAlpha *= 0.32;
  const shift = (distance * SKYLINE_PARALLAX) % 180;
  for (let i = -1; i * 180 - shift < WORLD_WIDTH; i++) {
    const base = i * 180 - shift;
    drawTowerCrane(ctx, base + 40);
    drawFrame(ctx, base + 120);
  }
  ctx.restore();
}

function drawTowerCrane(ctx: CanvasRenderingContext2D, x: number): void {
  const top = 26;
  const bottom = GROUND_Y - 6;
  ctx.beginPath();
  ctx.moveTo(x, top);
  ctx.lineTo(x, bottom);
  // Jib and counter-jib.
  ctx.moveTo(x - 16, top + 6);
  ctx.lineTo(x + 34, top + 6);
  // Cable and hook.
  ctx.moveTo(x + 24, top + 6);
  ctx.lineTo(x + 24, top + 22);
  ctx.moveTo(x + 21, top + 22);
  ctx.lineTo(x + 27, top + 22);
  ctx.stroke();

  // Mast lattice.
  ctx.beginPath();
  for (let y = top + 10; y < bottom; y += 12) {
    ctx.moveTo(x - 3, y);
    ctx.lineTo(x + 3, y + 6);
    ctx.moveTo(x + 3, y);
    ctx.lineTo(x - 3, y + 6);
  }
  ctx.stroke();
}

function drawFrame(ctx: CanvasRenderingContext2D, x: number): void {
  const top = GROUND_Y - 46;
  ctx.beginPath();
  ctx.rect(x, top, 34, 46);
  for (let y = top + 15; y < GROUND_Y; y += 15) {
    ctx.moveTo(x, y);
    ctx.lineTo(x + 34, y);
  }
  ctx.moveTo(x + 17, top);
  ctx.lineTo(x + 17, GROUND_Y);
  ctx.stroke();
}

/** The site floor: a solid line, scrolling gravel, and the odd cone. */
function drawGround(ctx: CanvasRenderingContext2D, distance: number): void {
  ctx.beginPath();
  ctx.moveTo(0, GROUND_Y);
  ctx.lineTo(WORLD_WIDTH, GROUND_Y);
  ctx.stroke();

  const shift = distance % 24;
  ctx.beginPath();
  for (let i = -1; i * 24 - shift < WORLD_WIDTH; i++) {
    const x = i * 24 - shift;
    ctx.moveTo(x, GROUND_Y + 5);
    ctx.lineTo(x + 9, GROUND_Y + 5);
    ctx.moveTo(x + 14, GROUND_Y + 10);
    ctx.lineTo(x + 20, GROUND_Y + 10);
  }
  ctx.stroke();

  const coneShift = distance % 120;
  for (let i = -1; i * 120 - coneShift < WORLD_WIDTH; i++) {
    drawCone(ctx, i * 120 - coneShift + 52);
  }
}

function drawCone(ctx: CanvasRenderingContext2D, x: number): void {
  ctx.beginPath();
  ctx.moveTo(x, GROUND_Y);
  ctx.lineTo(x + 3, GROUND_Y - 7);
  ctx.lineTo(x + 6, GROUND_Y);
  ctx.closePath();
  ctx.fill();
}

function drawObstacle(ctx: CanvasRenderingContext2D, obstacle: Obstacle): void {
  const gapBottom = obstacle.gapY + obstacle.gapH;
  const standing = GROUND_Y - gapBottom;

  if (obstacle.gapY > 2) {
    drawHanging(ctx, obstacle, obstacle.gapY);
  }
  if (standing > 2) {
    drawStanding(ctx, obstacle, gapBottom, standing);
  }
}

/** The part suspended from above — always reads as "hung from a crane". */
function drawHanging(
  ctx: CanvasRenderingContext2D,
  obstacle: Obstacle,
  height: number
): void {
  const { x, kind } = obstacle;
  const w = OBSTACLE_WIDTH;

  if (kind === "crane") {
    // Cable down to a suspended crate.
    const crateTop = Math.max(height - 26, 0);
    ctx.beginPath();
    ctx.moveTo(x + w / 2, 0);
    ctx.lineTo(x + w / 2, crateTop);
    ctx.stroke();
    ctx.beginPath();
    ctx.rect(x, crateTop, w, height - crateTop);
    ctx.moveTo(x, crateTop);
    ctx.lineTo(x + w, height);
    ctx.moveTo(x + w, crateTop);
    ctx.lineTo(x, height);
    ctx.stroke();
    return;
  }

  if (kind === "beam") {
    // I-beam: a web with flanges top and bottom.
    ctx.beginPath();
    ctx.rect(x + w / 2 - 4, 0, 8, height);
    ctx.rect(x, height - 7, w, 7);
    ctx.stroke();
    return;
  }

  drawScaffold(ctx, x, 0, w, height);
}

/** The part standing on the site floor. */
function drawStanding(
  ctx: CanvasRenderingContext2D,
  obstacle: Obstacle,
  top: number,
  height: number
): void {
  const { x, kind } = obstacle;
  const w = OBSTACLE_WIDTH;

  if (kind === "beam") {
    // Concrete pillar, hatched.
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, top, w, height);
    ctx.stroke();
    ctx.clip();
    ctx.beginPath();
    for (let i = -height; i < w + height; i += 7) {
      ctx.moveTo(x + i, top);
      ctx.lineTo(x + i + height, top + height);
    }
    ctx.globalAlpha *= 0.5;
    ctx.stroke();
    ctx.restore();
    return;
  }

  if (kind === "crane") {
    // Stacked crates, tallest at the bottom.
    ctx.beginPath();
    for (let y = GROUND_Y; y > top; y -= 16) {
      const boxTop = Math.max(y - 16, top);
      ctx.rect(x, boxTop, w, y - boxTop);
      ctx.moveTo(x, boxTop);
      ctx.lineTo(x + w, y);
    }
    ctx.stroke();
    return;
  }

  drawScaffold(ctx, x, top, w, height);
}

/** Scaffold tower: uprights, ledgers, and X bracing every bay. */
function drawScaffold(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number
): void {
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.stroke();

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.beginPath();
  for (let bay = y; bay < y + h; bay += 18) {
    ctx.moveTo(x, bay);
    ctx.lineTo(x + w, bay + 18);
    ctx.moveTo(x + w, bay);
    ctx.lineTo(x, bay + 18);
    ctx.moveTo(x, bay);
    ctx.lineTo(x + w, bay);
  }
  ctx.stroke();
  ctx.restore();
}

function drawDrone(ctx: CanvasRenderingContext2D, state: GameState): void {
  const cx = DRONE_X + DRONE_WIDTH / 2;
  const cy = state.drone.y + DRONE_HEIGHT / 2;
  // Nose down as it falls, up on the way from a flap — clamped so it never
  // tumbles.
  const tilt = Math.max(-0.35, Math.min(0.55, state.drone.vy / 340));

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(tilt);

  // Fuselage: a flat wedge, nose to the right. Kept shallow so it stays a
  // shape rather than a blob at this size.
  ctx.beginPath();
  ctx.moveTo(-11, -2);
  ctx.lineTo(6, -3);
  ctx.lineTo(10, 0);
  ctx.lineTo(6, 3);
  ctx.lineTo(-11, 2);
  ctx.closePath();
  ctx.fill();

  // Gimbal camera under the nose.
  ctx.beginPath();
  ctx.arc(7, 4, 1.8, 0, Math.PI * 2);
  ctx.fill();

  // Arms out to the rotor hubs, and the skids underneath.
  ctx.beginPath();
  ctx.moveTo(-5, -2);
  ctx.lineTo(-12, -9);
  ctx.moveTo(3, -2);
  ctx.lineTo(10, -9);
  ctx.moveTo(-7, 2);
  ctx.lineTo(-8, 6);
  ctx.moveTo(1, 2);
  ctx.lineTo(2, 6);
  ctx.moveTo(-11, 6);
  ctx.lineTo(5, 6);
  ctx.stroke();

  drawRotor(ctx, -12, -9, state);
  drawRotor(ctx, 10, -9, state);

  ctx.restore();
}

/**
 * A spinning blade read side-on: the visible span narrows and widens with the
 * rotation, which is enough to sell motion in one colour. Parked when idle.
 */
function drawRotor(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  state: GameState
): void {
  const spinning = state.phase === "playing";
  const phase = spinning ? state.elapsed * 26 + x : 0;
  const span = 6.5 * (spinning ? Math.abs(Math.cos(phase)) * 0.65 + 0.35 : 1);

  ctx.beginPath();
  ctx.moveTo(x - span, y - 0.5);
  ctx.lineTo(x + span, y - 0.5);
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, 1.2, 0, Math.PI * 2);
  ctx.fill();
}

function pad(value: number): string {
  return Math.min(value, 99_999).toString().padStart(5, "0");
}

function drawScore(
  ctx: CanvasRenderingContext2D,
  score: number,
  best: number
): void {
  ctx.save();
  ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  if (best > 0) {
    const base = ctx.globalAlpha;
    ctx.globalAlpha = base * 0.55;
    ctx.fillText(`HI ${pad(best)}`, WORLD_WIDTH - HUD_INSET - 54, 8);
    ctx.globalAlpha = base;
  }
  ctx.fillText(pad(score), WORLD_WIDTH - HUD_INSET, 8);
  ctx.restore();
}

function drawGameOver(ctx: CanvasRenderingContext2D): void {
  ctx.save();
  ctx.font = "bold 12px ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("G A M E   O V E R", WORLD_WIDTH / 2, WORLD_HEIGHT / 2 - 18);
  ctx.restore();
}
